import { useMemo } from 'react';
import { Spreadsheet } from '../../../spreadsheet';
import { useAllPaysForMonth } from '../../../../lib/supabase/pay/supabasePayService';
import { Card, CardContent } from '../../../ui/card';
import { useCosts } from '../../../../lib/supabase/costs/supabaseCostService';
import { DateTime } from 'luxon';
import { generatePaySummarySpreadsheetData } from './paySummaryCalculations';

const months = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

interface PaySummarySpreadsheetProps {
  defaultMonth?: number;
  defaultYear?: number;
  isFullscreen?: boolean;
}

export function PaySummarySpreadsheet({ defaultMonth, defaultYear, isFullscreen }: PaySummarySpreadsheetProps) {
  const monthNumber = defaultMonth !== undefined ? defaultMonth : DateTime.utc().month;
  const yearNumber = defaultYear !== undefined ? defaultYear : DateTime.utc().year;

  // Fetch data
  const { data: pays, isLoading: isPaysLoading } = useAllPaysForMonth(yearNumber, monthNumber);
  const { data: costs, isLoading: isCostsLoading } = useCosts(DateTime.fromObject({ year: yearNumber, month: monthNumber + 1 }));

  // Process data and calculate summaries
  const summaryData = useMemo(() => {
    if (!pays || !costs) return { data: [], columnCount: 2 };

    return generatePaySummarySpreadsheetData(pays, costs);
  }, [pays, costs]);

  // Cell styling function
  const getCellStyle = (rowIdx: number, _: string, value: any) => {
    // Headers row (row 0)
    if (rowIdx === 0) {
      return {
        fontWeight: 'bold' as const,
        fontSize: '14px',
        textAlign: 'center' as const,
        backgroundColor: '#f3f4f6'
      };
    }

    // Section headers (TOTAL, SET X)
    if (value === 'TOTAL' || value?.startsWith('SET ')) {
      return {
        fontWeight: 'bold' as const,
        fontSize: '13px',
        textAlign: 'center' as const
      };
    }

    // Main category headers
    if (value === 'Total Sales' || value === 'COST BREAKDOWN') {
      return {
        fontWeight: 'bold' as const,
        fontSize: '13px'
      };
    }

    // Sub-category headers
    if (value === 'Breakdown' || value === 'UK' || value === 'Filipino') {
      return {
        fontWeight: 'bold' as const,
        fontSize: '13px'
      };
    }

    if (value === 'Sales Fee' || value === 'OT Fee' || value === 'Bonus' || value === 'Scheduling' || value === 'Referrals' || value === 'MGMT' || value === 'Marketing' || value === 'Tools' || value === 'Commission' || value === 'Other Costs') {
      return {
        textAlign: 'right' as const
      };
    }

    // Individual cost items (indented with spaces)
    if (typeof value === 'string' && value.startsWith('  ') && !value.includes('£')) {
      return {
        textAlign: 'right' as const,
        fontSize: '11px',
        paddingLeft: '20px',
        color: '#6b7280',
        fontWeight: 400
      };
    }

    // Currency values (detect £ symbol)
    if (typeof value === 'string' && value.includes('£')) {
      // Check if this is a sub-item row by checking the row data
      const rowData = summaryData.data[rowIdx];
      const isSubItem = rowData && typeof rowData.A === 'string' && rowData.A.startsWith('  ');
      
      if (isSubItem) {
        return {
          textAlign: 'right' as const,
          fontSize: '11px',
          color: '#6b7280',
          fontWeight: 400
        };
      }
      
      return {
        textAlign: 'right' as const,
        fontWeight: 500,
        color: '#FF0000'
      };
    }

    // Add styling for INTERNAL COSTS and TOTAL COSTS
    if (value === 'INTERNAL COSTS' || value === 'TOTAL COSTS') {
      return {
        fontWeight: 'bold' as const,
        fontSize: '13px'
      };
    }

    // Regular text
    return {
      fontSize: '12px'
    };
  };

  const isLoading = isPaysLoading || isCostsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading pay summary...</div>
      </div>
    );
  }

  if (summaryData.data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No pay data found for {months[monthNumber].label} {yearNumber}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col">
            <Spreadsheet
              columns={summaryData.columnCount}
              minRows={summaryData.data.length}
              data={summaryData.data}
              editable={false}
              columnWidths={(() => {
                // TOTAL section: label (150) + amount (120) + gap (30), then repeat for each SET
                const widths = [140, 125, 25]; // TOTAL label, TOTAL amount, gap
                const setCount = Math.floor((summaryData.columnCount - 3) / 3);
                for (let i = 0; i < setCount; i++) {
                  widths.push(140, 125); // SET label, SET amount
                  if (i < setCount - 1) {
                    widths.push(30); // gap between sets (but not after the last set)
                  }
                }
                return widths;
              })()}
              rowHeight={35}
              fillHeight={true}
              isFullscreen={isFullscreen}
              buffers={{ fullscreen: 20, normal: 20 }}
              getCellStyle={getCellStyle}
              csvFilename={`pay-summary-${yearNumber}-${(monthNumber + 1).toString().padStart(2, '0')}.csv`}
              csvIncludeHeaders={true}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PaySummarySpreadsheet; 